import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, PassportApplication, PassportApplicationState } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const ALL_OFFICER_ROLES = ['PASSPORT_OFFICER', 'VETTING_OFFICER', 'PRODUCTION'];
const STATES: PassportApplicationState[] = [
  'APPLIED', 'BIOMETRICS', 'VETTING', 'APPROVED', 'PERSONALISATION', 'QC',
  'READY_FOR_COLLECTION', 'READY_FOR_DISPATCH', 'COLLECTED', 'DISPATCHED', 'REJECTED', 'CANCELLED',
];

type Tab = 'all' | 'vetting' | 'production';

/** Passports — the full M04 application workflow: biometrics, vetting, production, dispatch/collection. */
export function PassportApplications() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isAnyOfficer = roles.some((r) => ALL_OFFICER_ROLES.includes(r));
  const isVettingOfficer = roles.includes('VETTING_OFFICER');
  const isProduction = roles.includes('PRODUCTION');

  const [tab, setTab] = useState<Tab>('all');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (appliedQ) params.set('q', appliedQ);
    if (tab === 'all') {
      if (status) params.set('status', status);
      return `/api/v1/m04/applications?${params.toString()}`;
    }
    if (tab === 'vetting') return `/api/v1/m04/vetting/queue?${params.toString()}`;
    if (status) params.set('status', status);
    return `/api/v1/m04/production/queue?${params.toString()}`;
  }, [tab, status, appliedQ]);

  const listQ = useApiQuery<Page<PassportApplication>>(path);

  const tabs: Array<{ key: Tab; label: string }> = [{ key: 'all', label: 'All applications' }];
  if (isVettingOfficer) tabs.push({ key: 'vetting', label: 'Vetting queue' });
  if (isProduction) tabs.push({ key: 'production', label: 'Production queue' });

  return (
    <div>
      <PageHeader
        title="Passport Applications"
        subtitle="Apply → biometrics → vetting → production → dispatch/collection (M04)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-44" placeholder="Search NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
            {tab !== 'vetting' ? (
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            ) : null}
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>
        }
      />

      {tabs.length > 1 ? <TabStrip tabs={tabs} active={tab} onChange={setTab} /> : null}
      {!isAnyOfficer ? <RoleNotice roles={ALL_OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Type</th>
                <th className="table-head">Channel</th>
                <th className="table-head">Fee tier</th>
                <th className="table-head">Last updated</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{p.nin}</td>
                  <td className="table-cell">{p.type}</td>
                  <td className="table-cell">
                    {p.channel}
                    {p.missionCode ? <span className="text-xs text-slate-400"> · {p.missionCode}</span> : null}
                  </td>
                  <td className="table-cell text-xs text-slate-500">{p.feeTier}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(p.updatedAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <ApplicationDetailModal
          id={selectedId}
          roles={roles}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function VettingCheckRow({ label, result, detail }: { label: string; result: string; detail?: string | null }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
      <div>
        <div className="text-sm font-medium text-slate-700">{label}</div>
        {detail ? <div className="text-xs text-slate-400">{detail}</div> : null}
      </div>
      <StatusBadge status={result} />
    </div>
  );
}

function ApplicationDetailModal({
  id,
  roles,
  onClose,
  onChanged,
}: {
  id: string;
  roles: string[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const appQ = useApiQuery<PassportApplication>(`/api/v1/m04/applications/${id}`);
  const a = appQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Biometrics (PASSPORT_OFFICER)
  const [officeCode, setOfficeCode] = useState('');
  const [slot, setSlot] = useState('');
  // Vetting (VETTING_OFFICER)
  const [exitClear, setExitClear] = useState(true);
  const [exitNote, setExitNote] = useState('');
  const [decisionApprove, setDecisionApprove] = useState<boolean | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  // Production (PRODUCTION)
  const [qcPass, setQcPass] = useState<boolean | null>(null);
  const [qcNote, setQcNote] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  const isPassportOfficer = roles.includes('PASSPORT_OFFICER');
  const isVettingOfficer = roles.includes('VETTING_OFFICER');
  const isProduction = roles.includes('PRODUCTION');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={a ? `${a.type} · ${a.nin}` : 'Passport application'} onClose={onClose} wide>
      {appQ.error ? <ErrorNote message={appQ.error} /> : null}
      {appQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={a.state} />} />
            <Detail label="Channel" value={a.channel} />
            <Detail label="Fee tier" value={a.feeTier} />
            {a.priorPassportNo ? <Detail label="Prior passport" value={a.priorPassportNo} mono /> : null}
            {a.guardianNin ? <Detail label="Guardian NIN" value={a.guardianNin} mono /> : null}
            {a.payIntentRefNo ? <Detail label="Fee intent" value={a.payIntentRefNo} mono /> : null}
            <Detail label="Fee paid" value={a.feePaidAt ? formatDateTime(a.feePaidAt) : 'Not yet'} />
          </div>

          {(a.state === 'VETTING' || a.identityCheckedAt) ? (
            <div>
              <div className="field-label">Vetting sub-checks</div>
              <div className="space-y-2">
                <VettingCheckRow
                  label="M01 identity match"
                  result={a.identityMatchResult}
                  detail={a.identityMatchDetail ? JSON.stringify(a.identityMatchDetail) : undefined}
                />
                <VettingCheckRow label="M05 police flags" result={a.policeFlagsResult} detail={a.policeFlagsDetail ? JSON.stringify(a.policeFlagsDetail) : undefined} />
                <VettingCheckRow label="Court exit-restriction" result={a.exitRestrictionResult} detail={a.exitRestrictionNote ?? undefined} />
              </div>
            </div>
          ) : null}

          {a.rejectionReason ? <ErrorNote message={`Rejected: ${a.rejectionReason}`} /> : null}
          {a.cancelledReason ? <ErrorNote message={`Cancelled: ${a.cancelledReason}`} /> : null}
          {a.qcFailReason ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800">QC failed: {a.qcFailReason}</div>
          ) : null}

          {/* ── PASSPORT_OFFICER actions ─────────────────────────────────── */}
          {a.state === 'APPLIED' && a.channel === 'MISSION' ? (
            isPassportOfficer ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700">Record embassy-scheduled biometrics</div>
                <Field label="Slot (ISO datetime)">
                  <input className="field-input" placeholder="2026-08-01T09:00:00.000Z" value={slot} onChange={(e) => setSlot(e.target.value)} />
                </Field>
                <button
                  className="btn-primary"
                  disabled={busy || !slot}
                  onClick={() => call(() => api.post(`/api/v1/m04/applications/${id}/biometrics/mission-schedule`, { slot }))}
                >
                  Schedule
                </button>
              </div>
            ) : (
              <RoleNotice roles={['PASSPORT_OFFICER']} />
            )
          ) : null}

          {a.state === 'APPLIED' && a.channel === 'LOCAL' ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Book a C10 ServiceDesk biometrics appointment</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Office code">
                  <input className="field-input" placeholder="VICTORIA_CENTRAL" value={officeCode} onChange={(e) => setOfficeCode(e.target.value)} />
                </Field>
                <Field label="Slot (ISO datetime)">
                  <input className="field-input" placeholder="2026-08-01T09:00:00.000Z" value={slot} onChange={(e) => setSlot(e.target.value)} />
                </Field>
              </div>
              <button
                className="btn-primary"
                disabled={busy || !officeCode || !slot}
                onClick={() => call(() => api.post(`/api/v1/m04/applications/${id}/biometrics/book`, { officeCode, slot }))}
              >
                Book appointment
              </button>
            </div>
          ) : null}

          {a.state === 'BIOMETRICS' ? (
            isPassportOfficer ? (
              <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m04/applications/${id}/biometrics/complete`))}>
                Confirm biometrics captured — run vetting checks
              </button>
            ) : (
              <RoleNotice roles={['PASSPORT_OFFICER']} />
            )
          ) : null}

          {a.state === 'READY_FOR_COLLECTION' ? (
            isPassportOfficer ? (
              <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m04/applications/${id}/collect`))}>
                Confirm collected at counter
              </button>
            ) : (
              <RoleNotice roles={['PASSPORT_OFFICER']} />
            )
          ) : null}

          {(a.state === 'APPLIED' || a.state === 'BIOMETRICS') && isPassportOfficer ? (
            !showCancel ? (
              <button className="btn-danger" onClick={() => setShowCancel(true)}>
                Cancel application
              </button>
            ) : (
              <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                <Field label="Cancellation reason (optional)">
                  <input className="field-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                </Field>
                <div className="flex gap-2">
                  <button className="btn-secondary" onClick={() => setShowCancel(false)}>
                    Back
                  </button>
                  <button
                    className="btn-danger"
                    disabled={busy}
                    onClick={() => call(() => api.post(`/api/v1/m04/applications/${id}/cancel`, cancelReason ? { reason: cancelReason } : {}))}
                  >
                    Confirm cancel
                  </button>
                </div>
              </div>
            )
          ) : null}

          {/* ── VETTING_OFFICER actions ──────────────────────────────────── */}
          {a.state === 'VETTING' ? (
            isVettingOfficer ? (
              <div className="space-y-3 border-t border-slate-100 pt-4">
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m04/applications/${id}/vetting/run-checks`))}>
                    Re-run M01/M05 checks
                  </button>
                </div>

                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <div className="text-sm font-semibold text-slate-700">Court exit-restriction attestation</div>
                  <div className="flex items-center gap-3 text-sm">
                    <label className="flex items-center gap-1.5">
                      <input type="radio" checked={exitClear} onChange={() => setExitClear(true)} /> Clear
                    </label>
                    <label className="flex items-center gap-1.5">
                      <input type="radio" checked={!exitClear} onChange={() => setExitClear(false)} /> Hit
                    </label>
                  </div>
                  <Field label="Note (optional)">
                    <input className="field-input" value={exitNote} onChange={(e) => setExitNote(e.target.value)} />
                  </Field>
                  <button
                    className="btn-primary"
                    disabled={busy}
                    onClick={() =>
                      call(() =>
                        api.post(`/api/v1/m04/applications/${id}/vetting/exit-restriction`, {
                          clear: exitClear,
                          ...(exitNote.trim() ? { note: exitNote.trim() } : {}),
                        }),
                      )
                    }
                  >
                    Record attestation
                  </button>
                </div>

                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <div className="text-sm font-semibold text-slate-700">Final vetting decision</div>
                  <p className="text-xs text-slate-400">All three sub-checks must be resolved first. Approving over a HIT requires an override note.</p>
                  <div className="flex gap-2">
                    <button className={`btn-success ${decisionApprove === true ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecisionApprove(true)}>
                      Approve
                    </button>
                    <button className={`btn-danger ${decisionApprove === false ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecisionApprove(false)}>
                      Reject
                    </button>
                  </div>
                  {decisionApprove !== null ? (
                    <>
                      <Field label={decisionApprove ? 'Override note (required if any check is a HIT)' : 'Rejection reason (required)'}>
                        <textarea className="field-input" rows={2} value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} />
                      </Field>
                      <button
                        className={decisionApprove ? 'btn-success' : 'btn-danger'}
                        disabled={busy}
                        onClick={() =>
                          call(() =>
                            api.post(`/api/v1/m04/applications/${id}/vetting/decision`, {
                              approve: decisionApprove,
                              ...(decisionNote.trim() ? { note: decisionNote.trim() } : {}),
                            }),
                          )
                        }
                      >
                        {busy ? 'Submitting…' : `Confirm ${decisionApprove ? 'approval' : 'rejection'}`}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ) : (
              <RoleNotice roles={['VETTING_OFFICER']} />
            )
          ) : null}

          {/* ── PRODUCTION actions ───────────────────────────────────────── */}
          {a.state === 'APPROVED' ? (
            isProduction ? (
              <button className="btn-primary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m04/applications/${id}/personalise`))}>
                Start personalisation
              </button>
            ) : (
              <RoleNotice roles={['PRODUCTION']} />
            )
          ) : null}

          {a.state === 'PERSONALISATION' ? (
            isProduction ? (
              <button className="btn-primary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m04/applications/${id}/qc/submit`))}>
                Submit for QC
              </button>
            ) : (
              <RoleNotice roles={['PRODUCTION']} />
            )
          ) : null}

          {a.state === 'QC' ? (
            isProduction ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700">Quality control decision</div>
                <div className="flex gap-2">
                  <button className={`btn-success ${qcPass === true ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setQcPass(true)}>
                    Pass
                  </button>
                  <button className={`btn-danger ${qcPass === false ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setQcPass(false)}>
                    Fail
                  </button>
                </div>
                {qcPass !== null ? (
                  <>
                    <Field label="Note (optional)">
                      <input className="field-input" value={qcNote} onChange={(e) => setQcNote(e.target.value)} />
                    </Field>
                    <button
                      className={qcPass ? 'btn-success' : 'btn-danger'}
                      disabled={busy}
                      onClick={() =>
                        call(() => api.post(`/api/v1/m04/applications/${id}/qc/decision`, { pass: qcPass, ...(qcNote.trim() ? { note: qcNote.trim() } : {}) }))
                      }
                    >
                      {busy ? 'Submitting…' : qcPass ? 'Confirm pass — issue passport' : 'Confirm fail — back to personalisation'}
                    </button>
                  </>
                ) : null}
              </div>
            ) : (
              <RoleNotice roles={['PRODUCTION']} />
            )
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Applied {formatDateTime(a.createdAt)} · last updated {formatDateTime(a.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
